import Button from "@/components/button"
import EndShiftErrorSheet from "@/components/end-shift-error-sheet"
import EndShiftSheet from "@/components/end-shift-sheet"
import PickerWithSheet, {
  PickerButtonIndicator,
} from "@/components/picker-with-sheet"
import RoundedButton from "@/components/rounded-button"
import Topbar from "@/components/topbar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Icon } from "@/icons/icon"
import { FormParamStatus } from "@/lib/form/types"
import { posthog } from "@/lib/posthog"
import LogoutConfirmSheet from "@/components/logout-confirm-sheet"
import { useDebounceByHash, useDisableBack } from "@/lib/utils"
import AddDeptSheet from "@/packaging-v2/components/add-dept-sheet"
import AddItemSheet from "@/packaging-v2/components/add-item-sheet"
import ItemTabs, { ItemTabsEmpty } from "@/packaging-v2/components/item-tabs"
import PackagingV2Form from "@/packaging-v2/components/packaging-v2-form"
import { DeptToggleGroup } from "@/packaging/components/dept-toggle-group"
import { createEmptyItem, createMachineFormData } from "@/packaging-v2/lib/form"
import { handleNetworkError, handleOfflineActionError } from "@/services/base"
import {
  discardV2Shift,
  endV2Shift,
  getAllV2DeptSchemas,
  useSubmitV2Forms,
} from "@/services/packaging-v2"
import { getShiftStatus, setSession } from "@/services/session"
import useStore from "@/store"
import {
  MachineOption,
  PackagingV2Sheets,
  V2MachineFormData,
} from "@/store/packaging-v2"
import { ShiftStatus } from "@/store/session"
import { LabelValue } from "@/types/common"
import { HTTPError } from "ky"
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router"
import { toast } from "sonner"

export default function PackagingV2Page() {
  const {
    currDeptId,
    setCurrDeptId,
    currMachineId,
    setCurrMachineId,
    currItemIndex,
    setCurrItemIndex,
    ...packagingV2Store
  } = useStore.use.packagingV2()
  const sessionStore = useStore.use.session()
  const processSession = useStore.use.packagingV2().shift
  const userStore = useStore.use.user()
  const navigate = useNavigate()
  useDisableBack()
  const debounce = useDebounceByHash(500)

  const machines = useMachines()
  const depts = useDepts()
  const currentItems = useCurrentItems()
  const canEndShift = useCanEndShift()
  const isSheetSubmitted = useIsSheetSubmitted()

  const { isLoading, error } = useEnsureDataLoaded()
  const [showLogoutSheet, setShowLogoutSheet] = useState(false)

  useInit()
  useIndicators()

  function useInit() {
    useEffect(() => {
      // Initialize first item if needed
      initializeFirstItem()
    }, [currDeptId, currMachineId])

    useSubmitV2Forms()
  }

  function useEnsureDataLoaded() {
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const loadingRef = useRef(false) // Prevent duplicate fetches

    useEffect(() => {
      async function loadData() {
        // Check if data already loaded
        const hasSchemas = packagingV2Store.allDepartments.length > 0
        const hasSession = !!packagingV2Store.shift.processId

        if (hasSchemas && hasSession) {
          return // All data present
        }

        // Prevent duplicate requests
        if (loadingRef.current) {
          return
        }

        loadingRef.current = true
        setIsLoading(true)
        setError(null)

        try {
          // Fetch schemas and session in parallel
          const [schemasResult, sessionResult] = await Promise.allSettled([
            hasSchemas ? Promise.resolve(null) : getAllV2DeptSchemas(),
            hasSession ? Promise.resolve(null) : getShiftStatus(),
          ])

          // Handle schemas
          if (schemasResult.status === 'fulfilled' && schemasResult.value) {
            packagingV2Store.setAllSchemas(schemasResult.value)
          } else if (schemasResult.status === 'rejected') {
            throw new Error('Failed to load department schemas')
          }

          // Handle session
          if (sessionResult.status === 'fulfilled' && sessionResult.value) {
            setSession(sessionResult.value.shiftSession)

            // Find packaging v2 process in session
            const packagingProcess = sessionResult.value.shiftSession?.employeeSessions?.find(
              (proc) => proc.processName === "Packaging V2"
            )

            if (packagingProcess) {
              packagingV2Store.startShift({
                processId: packagingProcess.processId,
                shiftId: sessionResult.value.shiftSession!.shiftId,
              })
            }
          } else if (sessionResult.status === 'rejected') {
            throw new Error('Failed to load session data')
          }

        } catch (err) {
          console.error("Failed to load data:", err)
          posthog.captureException(err)

          const handled = await handleNetworkError(err, navigate)
          if (!handled) {
            setError(err instanceof Error ? err.message : "Failed to load packaging data")
          }
        } finally {
          setIsLoading(false)
          loadingRef.current = false
        }
      }

      void loadData()
    }, [])

    return { isLoading, error }
  }

  function initializeFirstItem() {
    if (!currDeptId || !currMachineId) return

    const formData = packagingV2Store.getFormData(currDeptId, currMachineId)
    if (formData && formData.items.length > 0) return

    // Create machine form data if not exists
    const deptSchema = packagingV2Store.getDeptSchema(currDeptId)
    const machine = deptSchema?.machines.find((m) => m.id === currMachineId)
    if (!machine) return

    const newFormData: V2MachineFormData = createMachineFormData(
      machine,
      currDeptId
    )

    // Add first empty item
    const firstItem = createEmptyItem(1)
    newFormData.items.push(firstItem)

    packagingV2Store.setMachineFormData(currDeptId, newFormData)
    setCurrItemIndex(0)
  }

  function useMachines() {
    const [machines, setMachines] = useState<MachineOption[]>([])

    useEffect(() => {
      const form = packagingV2Store.formSchemas.find(
        (item) => item.id === currDeptId
      )

      if (!form) {
        setMachines([])
        return
      }

      setMachines(
        form.machines.map((item) => ({
          id: item.id,
          label: item.name,
          value: item.id,
        }))
      )
    }, [currDeptId, packagingV2Store.formSchemas])

    return machines
  }

  function useDepts() {
    const [depts, setDepts] = useState<LabelValue[]>([])

    useEffect(() => {
      const newDepts = packagingV2Store.formSchemas.map((form) => ({
        id: form.id,
        label: form.name,
        value: form.id,
      }))
      setDepts(newDepts)

      if (!currDeptId && newDepts.length > 0) {
        setCurrDeptId(newDepts[0].value)
      }
    }, [packagingV2Store.formSchemas])

    return depts
  }

  function useCurrentItems() {
    return useMemo(() => {
      const formData = packagingV2Store.getFormData(currDeptId, currMachineId)
      return formData?.items || []
    }, [currDeptId, currMachineId, packagingV2Store.forms])
  }

  function useIsSheetSubmitted() {
    return useMemo(() => {
      const formData = packagingV2Store.getFormData(currDeptId, currMachineId)
      if (!formData || formData.items.length === 0) return false
      return formData.items.some(item => item.status === FormParamStatus.SUBMITTED)
    }, [currDeptId, currMachineId, packagingV2Store.forms])
  }

  function useIndicators() {
    function getUpdatedIndicators(
      forms: V2MachineFormData[]
    ): Record<string, PickerButtonIndicator> {
      const result: Record<string, PickerButtonIndicator> = {}

      for (const form of forms) {
        const deptId = form.deptId
        const machineId = form.id

        let hasSubmitted = false
        let hasDraft = false

        for (const item of form.items) {
          for (const section of item.sections) {
            if (section.status === FormParamStatus.SUBMITTED) {
              hasSubmitted = true
            } else if (
              section.status === FormParamStatus.DRAFT ||
              section.parameters.some((p) => p.value || p.windows?.some((w) => w.value))
            ) {
              hasDraft = true
            }
          }
        }

        if (hasSubmitted && !hasDraft) {
          result[`${deptId}.${machineId}`] = PickerButtonIndicator.Success
        } else if (hasSubmitted || hasDraft) {
          result[`${deptId}.${machineId}`] = PickerButtonIndicator.Warning
        }
      }

      return result
    }

    useEffect(() => {
      debounce("updateIndicators", () => {
        const updated = getUpdatedIndicators(packagingV2Store.forms)
        packagingV2Store.setIndicators(updated)
      })
    }, [packagingV2Store.forms])
  }

  function useCanEndShift() {
    const [canEnd, setCanEnd] = useState(false)

    useEffect(() => {
      for (const form of packagingV2Store.forms) {
        for (const item of form.items) {
          for (const section of item.sections) {
            if (section.status === FormParamStatus.SUBMITTED) {
              setCanEnd(true)
              return
            }
          }
        }
      }
      setCanEnd(false)
    }, [packagingV2Store.forms])

    return canEnd
  }

  function handleChangeDept(value: string) {
    setCurrDeptId(value)
    setCurrItemIndex(0)
  }

  function openAddDeptSheet() {
    packagingV2Store.setCurrSheet(PackagingV2Sheets.AddDept)
  }

  function canEdit(): boolean {
    const process = sessionStore.session.processes.find(
      (proc) => proc.processId === processSession.processId
    )
    if (!process) return false

    return (
      process.status === ShiftStatus.Started ||
      process.status === ShiftStatus.Draft
    )
  }

  function handleChangeMachine(machineId: string) {
    setCurrMachineId(machineId)
    setCurrItemIndex(0)
  }

  function handleChangeItem(index: number) {
    setCurrItemIndex(index)
  }

  function handleAddItem() {
    packagingV2Store.setCurrSheet(PackagingV2Sheets.AddItem)
  }

  function handleTriggerDiscardShift() {
    packagingV2Store.setCurrSheet(PackagingV2Sheets.DiscardShift)
  }

  async function handleDiscardShift() {
    try {
      await discardV2Shift()
      await navigate("/manage-processes")
    } catch (err) {
      posthog.captureException(err)
      if (handleOfflineActionError(err, { action: "discard", target: "shift" })) {
        return
      }

      const handled = await handleNetworkError(err, navigate)

      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)
        return
      }

      console.error(err)
      toast.error("Error discarding shift")
    }
  }

  async function handleEndShift() {
    try {
      if (packagingV2Store.submissions.length) {
        packagingV2Store.setCurrSheet(PackagingV2Sheets.EndShiftLoading)
        return
      }

      await endV2Shift()
      closeAllSheets()
      sessionStore.endProcess(processSession.processId)
      packagingV2Store.removePendingSheets()
      await navigate("/shift-ended")
    } catch (err) {
      closeAllSheets()
      if (handleOfflineActionError(err, { action: "end", target: "shift" })) {
        return
      }

      const handled = await handleNetworkError(err, navigate)
      posthog.captureException(err)

      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)
        return
      }

      toast.error("Error ending shift. Please contact support")
    }
  }

  function triggerEndShift() {
    const indicator = Object.values(packagingV2Store.indicators).reduce(
      (acc, curr) => {
        if (acc === PickerButtonIndicator.Warning) {
          return PickerButtonIndicator.Warning
        }
        return curr
      },
      PickerButtonIndicator.Success as PickerButtonIndicator
    )

    if (indicator === PickerButtonIndicator.Warning) {
      packagingV2Store.setCurrSheet(PackagingV2Sheets.EndShiftError)
      return
    }

    packagingV2Store.setCurrSheet(PackagingV2Sheets.ConfirmEndShift)
  }

  function closeAllSheets() {
    packagingV2Store.setCurrSheet(PackagingV2Sheets.None)
  }

  async function handleClickPrevHistory() {
    await navigate("/packaging-v2/history")
  }

  async function handleClickBack() {
    void getShiftStatus().then((res) => {
      if (res.shiftSession) {
        setSession(res.shiftSession)
      }
    })

    await navigate("/manage-processes")
  }

  // Loading State - Full Page Spinner
  if (isLoading) {
    return (
      <div className="bg-backdrop h-dvh overflow-auto">
        <Topbar
          className="sticky top-0 z-10"
          title="Packaging"
          subtitle={userStore.plant.name}
          showBack
          onClickBack={handleClickBack}
        />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="text-label-text text-lg">Loading packaging data...</div>
          </div>
        </div>
      </div>
    )
  }

  // Error State - Error Message with Go Back Button
  if (error) {
    return (
      <div className="bg-backdrop h-dvh overflow-auto">
        <Topbar
          className="sticky top-0 z-10"
          title="Packaging"
          subtitle={userStore.plant.name}
          showBack
          onClickBack={handleClickBack}
        />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <div className="text-center flex flex-col gap-4 max-w-md px-4">
            <Icon name="NetworkError" className="h-12 w-12 text-error mx-auto" />
            <div className="text-error text-lg font-medium">{error}</div>
            <Button onClick={handleClickBack} className="mt-2">
              Go Back to Processes
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Normal State - Original UI
  return (
    <div className="bg-backdrop h-dvh overflow-auto">
      <Topbar
        className="sticky top-0 z-10"
        title="Packaging"
        subtitle={userStore.plant.name}
        showBack
        onClickBack={handleClickBack}
      >
        <div className="flex items-center gap-5">
          {!!packagingV2Store.formSchemas.length && canEdit() && (
            <RoundedButton
              icon={<Icon name="Plus" />}
              onClick={openAddDeptSheet}
            >
              Department
            </RoundedButton>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Icon name="MoreVertical" className="text-button-black h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent inPortal>
              <DropdownMenuItem onClick={handleClickPrevHistory}>
                View Previous History
              </DropdownMenuItem>

              {canEdit() && (
                <>
                  {canEndShift && (
                    <DropdownMenuItem onClick={triggerEndShift}>
                      End Process
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleTriggerDiscardShift}>
                    Discard and Exit
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem onClick={() => setShowLogoutSheet(true)}>Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </Topbar>

      <div className="py-4 px-4">
        {/* Department Tabs */}
        <DeptToggleGroup
          className="mb-6 mx-auto"
          type="single"
          options={depts}
          defaultValue={depts.at(0)?.value}
          value={currDeptId}
          onValueChange={handleChangeDept}
        />

        {/* Machine Picker */}
        {machines.length ? (
          <PickerWithSheet
            className="mb-4"
            options={machines}
            value={currMachineId}
            deptId={currDeptId}
            indicators={packagingV2Store.indicators}
            onChange={handleChangeMachine}
          />
        ) : (
          <div className="text-center flex flex-col gap-4 items-center justify-center">
            <div className="text-label-text">
              Please select a department or add a new one
            </div>

            {canEdit() && (
              <RoundedButton
                icon={<Icon name="Plus" />}
                onClick={openAddDeptSheet}
              >
                Department
              </RoundedButton>
            )}
          </div>
        )}

        {/* Item Tabs */}
        {machines.length > 0 && currMachineId && (
          <div className="mb-4">
            {currentItems.length > 0 ? (
              <ItemTabs
                items={currentItems}
                currentIndex={currItemIndex}
                disabled={!canEdit()}
                disableAdd={isSheetSubmitted}
                onChange={handleChangeItem}
                onAdd={handleAddItem}
              />
            ) : (
              !isSheetSubmitted && <ItemTabsEmpty onAdd={handleAddItem} />
            )}
          </div>
        )}

        {/* Main Form */}
        {currMachineId && <PackagingV2Form canEdit={canEdit()} />}
      </div>

      {/* Sheets */}
      <AddDeptSheet
        open={packagingV2Store.currSheet === PackagingV2Sheets.AddDept}
        onOpenChange={(open) => !open && closeAllSheets()}
      />
      <AddItemSheet
        open={packagingV2Store.currSheet === PackagingV2Sheets.AddItem}
        onOpenChange={(open) => !open && closeAllSheets()}
      />
      <EndShiftSheet
        open={packagingV2Store.currSheet === PackagingV2Sheets.ConfirmEndShift}
        title="End Process"
        description="You are making the end of your process for today."
        pendingDescription="You are making the end of your process for today. Please select a reason below"
        onClickBack={closeAllSheets}
        onClickConfirm={handleEndShift}
      />
      <EndShiftSheet
        open={packagingV2Store.currSheet === PackagingV2Sheets.DiscardShift}
        title="Discard Process"
        description="Are you sure you want to Discard and Exit? This will permanently delete your current process data."
        onClickBack={closeAllSheets}
        onClickConfirm={handleDiscardShift}
      />
      <EndShiftErrorSheet
        open={packagingV2Store.currSheet === PackagingV2Sheets.EndShiftError}
        onClickBack={closeAllSheets}
      />
      <LogoutConfirmSheet
        open={showLogoutSheet}
        onOpenChange={setShowLogoutSheet}
      />
    </div>
  )
}
